import { useState } from 'react';
import { useKanbanBoard } from '@/hooks/useKanbanBoard';
import { Button } from '@/components/ui/button';
import { Plus, Trash } from 'lucide-react';
import { getIconByName } from '@/utils/statusPriorityUtils';
import TaskCardWrapper from './TaskCardWrapper';
import AddTaskModal from './AddTaskModal';
import CreateMFOModal from '../mfo/CreateMFOModal';
import { KanbanColumn as ColumnType, KanbanCard } from '@/types/kanban';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface KanbanColumnProps {
  column: ColumnType;
  isActive?: boolean;
  isColumnReorderMode?: boolean;
  onDragStart?: (card: any) => void;
  onDragOver?: (columnId: string) => void;
  onDrop?: (columnId: string) => void;
  onAddTask?: (columnId: string) => void;
  onDeleteTask?: (taskId: string) => void;
  onTransferTask?: (taskId: string) => void;
  departments?: {id: string, name: string}[]; // Added for CreateMFOModal
  mfoCodes?: {id: string, code: string, title: string}[]; // Added for CreateMFOModal
  employees?: {id: string, name: string, avatar?: string}[]; // Added for CreateMFOModal
  activeDragType?: 'column' | 'card' | null;
  activeDragItem?: any;
  isDraggingOverlay?: boolean;
}

function toSlug(str: string) {
  return str?.toLowerCase().replace(/\s+/g, '-').trim();
}

const KanbanColumn = ({ 
  column,
  isActive,
  isColumnReorderMode,
  onDragStart,
  onDragOver,
  onDrop,
  onAddTask,
  onDeleteTask,
  onTransferTask,
  departments = [],
  mfoCodes = [],
  employees = [],
  activeDragType = null,
  activeDragItem = null,
  isDraggingOverlay = false
}: KanbanColumnProps) => {
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isCreateMFOModalOpen, setIsCreateMFOModalOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const { deleteCard, refreshData, moveCard } = useKanbanBoard();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Set up sortable for column reordering
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: column.id,
    disabled: !isColumnReorderMode
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  
  const Icon = getIconByName(column.iconName || 'Circle');
  
  // Track if a card is being dragged over this column
  const [isCardOver, setIsCardOver] = useState(false);
  
  // Handle column-level events
  const handleDragOver = (e: React.DragEvent) => {
    if (!isColumnReorderMode && onDragOver) {
      e.preventDefault();
      setIsDragOver(true);
      setIsCardOver(true);
      onDragOver(column.id);
    }
  };
  
  const handleDragLeave = () => {
    setIsDragOver(false);
    setIsCardOver(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    if (!isColumnReorderMode && onDrop) {
      e.preventDefault();
      setIsDragOver(false);
      setIsCardOver(false);
      onDrop(column.id);
    }
  };
  
  const handleAddTaskClick = () => {
    setIsCreateMFOModalOpen(true);
  };
  
  // Handle MFO creation
  const handleMFOCreated = async (mfo) => {
    const newCard = {
      id: mfo.id,
      mfo_id: mfo.id,
      title: mfo.title,
      description: mfo.successIndicators?.join('\n') || '',
      priority: mfo.priority,
      due_date: mfo.dueDate,
      column_id: column.id,
      // Add any other necessary fields
    };
    await supabase.from('kanban_cards').insert(newCard);
    refreshData();
  };
  
  // Add this handler inside KanbanColumn
  const handleTransferTask = (cardId: string, newColumnId: string) => {
    moveCard(cardId, newColumnId);
  };
  
  // Helper to get Not Started column id
  const getNotStartedColumnId = () => {
    if (toSlug(column.title) === 'not-started') return column.id;
    return null;
  };
  
  const handleDeleteColumn = async () => {
    setIsDeleting(true);
    try {
      // 1. Find the Not Started column (by slug)
      const { data: allColumns, error: colError } = await supabase
        .from('kanban_columns')
        .select('id, title');
      if (colError) throw colError;
      const notStartedCol = allColumns.find(col => toSlug(col.title) === 'not-started');
      if (!notStartedCol) throw new Error('Not Started column not found');

      // 2. Move all cards in this column to Not Started
      if (column.cards && column.cards.length > 0) {
        for (const card of column.cards) {
          // Update card's column_id
          await supabase
            .from('kanban_cards')
            .update({ column_id: notStartedCol.id })
            .eq('id', card.id);
          // If card is linked to MFO, update MFO status
          if ((card as any).mfo_id) {
            await supabase
              .from('mfo_items')
              .update({ status: 'not-started' })
              .eq('id', (card as any).mfo_id);
          }
        }
      }
      // 3. Delete the column
      await supabase
        .from('kanban_columns')
        .delete()
        .eq('id', column.id);
      // 4. Refresh board
      refreshData();
      setIsDeleteDialogOpen(false);
    } catch (err) {
      // Optionally show a toast
      // toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // The class for the column container changes based on whether we're in column reorder mode
  const columnClassName = `flex flex-col h-full min-w-[300px] max-w-[300px] bg-card rounded-lg border ${
    isActive ? 'ring-2 ring-primary' : ''
  } ${
    isDragOver ? 'ring-2 ring-primary shadow-lg bg-primary/5' : ''
  }`;
  
  // Animate column if being dragged as overlay
  const overlayStyle = isDraggingOverlay ? {
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    transform: 'scale(1.04)',
    opacity: 0.95,
    zIndex: 1000,
    background: '#fff',
    border: '2px solid #3b82f6',
  } : {};
  
  return (
    <div 
      ref={setNodeRef}
      style={{ ...style, ...overlayStyle }}
      className={columnClassName}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-column-id={column.id}
      {...(isColumnReorderMode ? { ...attributes, ...listeners } : {})}
    >
      <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
        <div className="flex items-center gap-2">
          <Icon style={{ color: column.iconColor || undefined }} className="h-5 w-5" />
          <h3 className="font-semibold">
            {column.title} {column.cards?.length > 0 && `(${column.cards.length})`}
          </h3>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={handleAddTaskClick}>
            <Plus className="h-4 w-4" />
          </Button>
          {/* Delete column button, only if not Not Started */}
          {toSlug(column.title) !== 'not-started' && (
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-destructive">
                  <Trash className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Column</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete the column "{column.title}"? <br />
                    All MFOs and cards in this column will be moved to "Not Started".
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteColumn} disabled={isDeleting} className="bg-destructive text-white">
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      
      <div className={`flex-1 overflow-y-auto p-2 kanban-column-content transition-colors duration-200 ${isCardOver ? 'bg-primary/10' : ''}`} style={{ maxHeight: 'calc(100vh - 260px)' }}>
        <SortableContext items={column.cards.map(card => card.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {column.cards && column.cards.length > 0 ? (
              column.cards.map((card) => (
                <TaskCardWrapper
                  key={card.id}
                  card={card as KanbanCard}
                  columnId={column.id}
                  onDelete={onDeleteTask || deleteCard}
                  onDragStart={onDragStart}
                  onTransfer={handleTransferTask}
                  activeDragType={activeDragType}
                  activeDragItem={activeDragItem}
                />
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center p-4">
                No tasks in this column
              </p>
            )}
          </div>
        </SortableContext>
      </div>
      
      {/* Keep the old AddTaskModal for backward compatibility */}
      <AddTaskModal
        isOpen={isAddTaskModalOpen}
        onOpenChange={setIsAddTaskModalOpen}
        columnId={column.id}
        newTaskColumn={column.id}
        onAddTask={() => {}}
        newTask={{
          title: "",
          description: "",
          priority: "medium",
          dueDate: new Date().toISOString().split('T')[0],
          assignees: []
        }}
        setNewTask={() => {}}
      />

      {/* Add the CreateMFOModal */}
      <CreateMFOModal
        isOpen={isCreateMFOModalOpen}
        onOpenChange={setIsCreateMFOModalOpen}
        mode="create"
        departments={departments}
        mfoCodes={mfoCodes}
        employees={employees}
        onMFOCreated={handleMFOCreated}
        onSuccess={() => setIsCreateMFOModalOpen(false)}
      />
    </div>
  );
};

export default KanbanColumn;